<template>
  <div class="catalog-form status-workspaces">
    <h1>
      <i class="fal fa-arrow-circle-left" @click="onCancel" title="Regresar"></i>
      <span class="text-truncate" v-if="action === crudActions.ACTIVE">Activar dominios de cliente: {{ name }}</span>
      <span class="text-truncate" v-if="action === crudActions.SUSPEND">Suspender dominios de cliente: {{ name }}</span>
      <span class="text-truncate" v-if="action === crudActions.INACTIVE">Baja de dominios de cliente: {{ name }}</span>
    </h1>

    <div class="form-area">
      <b-form v-show="!loading">
        <b-card :header="'Seleccione los dominios que desea ' + actionToPerform" class="form-section workspaces-list-item">
          <p v-if="workspaces && !workspaces.length">Este cliente no cuenta con ningún dominio relacionado.</p>
          <b-list-group>
            <b-list-group-item v-for="workspace in workspaces" :key="workspace.id">
              <div class="selector">
                <b-form-checkbox
                  style="cursor: pointer"
                  size="lg"
                  v-model="workspace._selected"
                  :disabled="isWorkspaceDisabled(workspace)">
                </b-form-checkbox>
              </div>
              <div class="name text-truncate">{{ workspace.domain.name }} ({{ getStatusDescription(workspace.status) }})</div>
            </b-list-group-item>
          </b-list-group>

          <b-alert show variant="warning" class="mt-3" v-if="selectionCount && action === crudActions.SUSPEND">
            <p>Al realizar esta acción los dominios seleccionados serán suspendidos para la razón social <b>{{ name }}</b> y los usuarios relacionados a ese cliente no podrán acceder a la plataforma.</p>
            <p v-if="ownerInSelection">En particular para {{ ownerForDomainNames.length > 1 ? 'los dominios': 'el dominio' }} <b>{{ ownerForDomainNamesString }}</b> todas las licencias de todas las razones sociales del dominio serán suspendidas ya que la razón social <b>{{ name }}</b> es dueña del dominio.</p>
            <b-form-checkbox v-model="userConfirmation">Entendido, deseo continuar</b-form-checkbox>
          </b-alert>

          <b-alert show variant="warning" class="mt-3" v-if="selectionCount && action === crudActions.INACTIVE">
            <p>Al realizar esta acción los dominios seleccionados serán dados de baja para la razón social <b>{{ name }}</b> y los usuarios relacionados a ese cliente no podrán acceder a la plataforma.</p>
            <p v-if="ownerInSelection">En particular para {{ ownerForDomainNames.length > 1 ? 'los dominios': 'el dominio' }} <b>{{ ownerForDomainNamesString }}</b> todas las licencias de todas las razones sociales del dominio serán dadas de baja ya que la razón social <b>{{ name }}</b> es dueña del dominio.</p>
            <b-form-checkbox
              v-model="userConfirmation"
              @change="changeFirstConfirmation"
            >
              Entendido, deseo continuar
            </b-form-checkbox>
            <b-form-checkbox
              v-if="userConfirmation && ecosystemOwnerInDomains.length"
              v-model="confirmationToInactiveWorkspaceOwner"
            >
              Estoy completamente seguro para dar de baja {{ ecosystemOwnerInDomains.length > 1 ? 'los dominios': 'el dominio' }} de tipo <em>Ecosistema</em> donde esta razón social es dueña.
            </b-form-checkbox>
          </b-alert>

          <b-alert show variant="warning" class="mt-3" v-if="selectionCount && action === crudActions.ACTIVE">
            <p>Al realizar esta acción todos los dominios seleccionados serán activados y los usuarios relacionados a ese cliente podrán acceder a la plataforma.</p>
            <p v-if="ownerInSelection">En particular para {{ ownerForDomainNames.length > 1 ? 'los dominios': 'el dominio' }} <b>{{ ownerForDomainNamesString }}</b> todas las licencias de todas las razones sociales del dominio serán activadas ya que la razón social <b>{{ name }}</b> es dueña del dominio.</p>
            <b-form-checkbox v-model="userConfirmation">Entendido, deseo continuar</b-form-checkbox>
          </b-alert>

          <b-alert
            :show="isAllWorkspacesActive && action === crudActions.ACTIVE"
            variant="info"
            class="pb-0 mt-2">
            <p>Todos los workspaces se encuentran <strong>activos</strong>, por lo cual ninguno puede seleccionarse para intentar activar.</p>
          </b-alert>
          <b-alert
            :show="isAllWorkspacesSuspended && action === crudActions.SUSPEND"
            variant="info"
            class="pb-0 mt-2">
            <p>Todos los workspaces se encuentran <strong>suspendidos</strong>, por lo cual ninguno puede seleccionarse para intentar suspender.</p>
          </b-alert>
        </b-card>

        <!-- ACCIONES -->
        <div class="text-right">
          <b-button class="mr-2" size="lg" @click="onCancel">Cancelar</b-button>
          <b-button variant="primary" size="lg" @click="activeItems" v-if="action === crudActions.ACTIVE" :disabled="!selectionCount || !userConfirmation">
            Activar seleccionados</b-button>
          <b-button variant="danger" size="lg" @click="suspendItems" v-if="action === crudActions.SUSPEND" :disabled="!selectionCount || !userConfirmation">
            Suspender seleccionados</b-button>
          <b-button variant="danger" size="lg" @click="inactiveItems" v-if="action === crudActions.INACTIVE"
            :disabled="
              !selectionCount ||
                (ecosystemOwnerInDomains.length
                  ? !userConfirmation || !confirmationToInactiveWorkspaceOwner
                  : !userConfirmation)"
          >
            Dar de baja seleccionados
          </b-button>
        </div>
      </b-form>

      <LoadingMask :active="loading"/>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator'
import DateSelector from '@/components/generic/DateSelector.vue'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import catalogsService from '@/services/CatalogsService'
import messageService from '@/services/support/MessageService'
import { PageRequest } from '@/types/Support'
import { ClientWorkspacesFilters, CrudActions, Workspace, WorkspaceStatus, ClientStatus, WorkspacesAction, Domain, DomainType } from '@/types/Catalogs'
import { AxiosError } from 'axios'
import userInfoStore from '@/store/userInfo'
import { RoleNames } from '@/types/Auth'

@Component({
  components: {
    DateSelector,
    LoadingMask
  }
})
export default class AdminClientWorkspacesForm extends Vue {
  @Prop({ required: true, default: 0 })
  id: number

  @Prop({ required: true, default: '' })
  name: string

  @Prop({ required: false, default: () => [] })
  domains: Domain[]

  @Prop({ required: true })
  action: CrudActions

  actionToPerform = ''

  PAGE_SIZE = 500
  loading = false
  userConfirmation = false
  confirmationToInactiveWorkspaceOwner = false
  workspaces: any[] = []
  crudActions = CrudActions

  /**
   * @return true if the user has the 'financesControl' role, false otherwise
   */
  get isFinancesControl (): boolean {
    return userInfoStore.hasRole(RoleNames.FINANCES_CONTROL)
  }

  get selectionCount () {
    return this.workspaces.filter((it: any) => it._selected).length
  }

  get ownerInSelection () {
    return this.workspaces.find((it: any) => it._selected && it.owner)
  }

  /**
   * Get a list of domain names where the current client is the owner
   */
  get ownerForDomainNames (): (string|undefined)[] {
    return this.ownerInDomains().map((workspace: Workspace) => workspace.domain.name)
  }

  /**
   * Get a string list of domain names where the current client is the owner
   */
  get ownerForDomainNamesString (): string {
    return this.ownerForDomainNames.join(', ')
  }

  /**
   * Get the list of workspaces where the current client si the owner
   */
  ownerInDomains (): Workspace[] {
    const owners = this.workspaces
      .filter((workspace) => workspace._selected && workspace.owner)
    return owners ?? []
  }

  /**
   * Get all the ecosystem domains that are content in the workspaces selected
   */
  get ecosystemOwnerInDomains (): Workspace[] {
    return this.ownerInDomains().filter(workspace => {
      let domain = this.domains.find(domain => domain.id === workspace.domain.id)
      if (domain) return domain.domainType === DomainType.ECOSYSTEM
    })
  }

  changeFirstConfirmation (): void {
    this.confirmationToInactiveWorkspaceOwner = false
  }

  /**
   * Validates if all the workspaces have the same status
   *
   * @param status
   */
  isAllWorkspacesWithStatus (status: WorkspaceStatus) {
    if (this.workspaces && this.workspaces.length) {
      return this.workspaces.every(w => w.status as WorkspaceStatus === status)
    }
    return false
  }

  /**
   * @returns true if all the workspaces are active, false otherwise
   */
  get isAllWorkspacesActive () {
    return this.isAllWorkspacesWithStatus(WorkspaceStatus.ACTIVE)
  }

  /**
   * @returns true if all the workspaces are suspended, false otherwise
   */
  get isAllWorkspacesSuspended () {
    return this.isAllWorkspacesWithStatus(WorkspaceStatus.SUSPENDED)
  }

  /**
   * @returns true if all the workspaces are inactive, false otherwise
   */
  get isAllWorkspacesInactive () {
    return this.isAllWorkspacesWithStatus(WorkspaceStatus.INACTIVE)
  }

  mounted () {
    switch (this.action) {
      case CrudActions.ACTIVE:
        this.actionToPerform = 'activar'
        break
      case CrudActions.SUSPEND:
        this.actionToPerform = 'suspender'
        break
      case CrudActions.INACTIVE:
        this.actionToPerform = 'dar de baja'
        break
    }
    if (this.id) {
      this.loading = true
      let pageRequest = new PageRequest<ClientWorkspacesFilters>(this.PAGE_SIZE)
      pageRequest.filters.clientId = this.id
      this.loadPage(pageRequest)
    } else {
      this.onCancel()
    }
  }

  loadPage (pageRequest: PageRequest<ClientWorkspacesFilters>) {
    catalogsService.getWorkspacesByClients(pageRequest).then(response => {
      const data = response.results || []
      data.forEach((it: any) => { it._selected = false })
      this.workspaces = response.results || []
      this.loading = false
    }).catch(() => {
      this.loading = false
    })
  }

  /**
   * Validates if the workspace will be able to selected
   *
   * @param workspace to check
   */
  isWorkspaceDisabled (workspace: Workspace) {
    // Attempt to change from active to active is not possible
    if (this.action === CrudActions.ACTIVE &&
    workspace.status === WorkspaceStatus.ACTIVE) return true
    // Attempt to inactive workspaces
    if (this.action === CrudActions.INACTIVE) {
      // It cannot change from inactive to inactive
      if (workspace.status === WorkspaceStatus.INACTIVE) return true
      const domain = this.domains.find((domain) => domain.id === workspace.domain.id)
      // Individual domains everyone with permissions can modify
      if (domain && domain.domainType === DomainType.INDIVIDUAL) return false
      // if it is owner and the user does not have the financesControl role
      if (workspace.owner && !this.isFinancesControl) return true
    }
    // Change status from inactive, suspended to suspended is not posible
    if (this.action === CrudActions.SUSPEND &&
    workspace.status !== WorkspaceStatus.ACTIVE) return true
    return false
  }

  getStatusDescription (status: string) {
    return Workspace.getStatusDescription(status)
  }

  async activeItems () {
    const workspaces = this.workspaces.filter((it: any) => it._selected)
    for (let it of workspaces) {
      try {
        await catalogsService.saveWorkspaceStatus(it.id, WorkspaceStatus.ACTIVE)
        it.status = WorkspaceStatus.ACTIVE
        it._selected = false
      } catch (error) {
        this.showApiError(error as any)
      }
    }

    // In case that some workspace had been activated, we active the client
    const someWorkspaceWasActivated = this.workspaces
      .some((workspace: Workspace) => workspace.status === WorkspaceStatus.ACTIVE)
    this.$emit(
      'saved',
      someWorkspaceWasActivated
        ? ClientStatus.ACTIVO
        : '',
      WorkspacesAction.ACTIVATION
    )
  }

  async suspendItems () {
    const workspaces = this.workspaces.filter((it: any) => it._selected)
    for (let it of workspaces) {
      try {
        await catalogsService.saveWorkspaceStatus(it.id, WorkspaceStatus.SUSPENDED)
        it.status = WorkspaceStatus.SUSPENDED
        it._selected = false
      } catch (error) {
        this.showApiError(error as any)
      }
    }

    const countByStatus = this.workspaces.filter((it: any) => it.status !== WorkspaceStatus.SUSPENDED).length
    this.$emit('saved', countByStatus === 0 ? ClientStatus.SUSPENDIDO : '', WorkspacesAction.SUSPENSION)
  }

  async inactiveItems () {
    const workspaces = this.workspaces.filter((it: any) => it._selected)
    for (let it of workspaces) {
      try {
        await catalogsService.inactiveWorkspace(it.id)
        it.status = WorkspaceStatus.INACTIVE
        it._selected = false
      } catch (error) {
        this.showApiError(error as any)
      }
    }

    const countByStatus = this.workspaces.filter((it: any) => it.status !== WorkspaceStatus.INACTIVE).length
    this.$emit('saved', countByStatus === 0 ? ClientStatus.BAJA : '', WorkspacesAction.INACTIVATION)
  }

  showApiError (error: AxiosError) {
    let data: any = error.response?.data
    messageService.error('Ocurrió un error', `${data.description} - ${data.message}`)
  }

  onCancel () {
    this.$emit('close')
  }
}
</script>
